const en = {
  /* =========================================================
     NAVIGATION
  ========================================================= */

  nav: {
    home: "Home",
    services: "Services",
    projects: "Projects",
    about: "About",
    contact: "Contact",
    login: "Login",
  },

  /* =========================================================
     COMMON
  ========================================================= */

  common: {
    learnMore: "Learn more",
    viewMore: "View more",
    discover: "Discover",
    contactUs: "Contact us",
    getStarted: "Get started",
    send: "Send",
    submit: "Submit",
    cancel: "Cancel",
    close: "Close",
    save: "Save",
    delete: "Delete",
    edit: "Edit",
    loading: "Loading...",
    error: "An error occurred.",
    success: "Success",
    back: "Back",
    next: "Next",
    previous: "Previous",
  },

  /* =========================================================
     CLIENT AREA
  ========================================================= */

  clientSpace: {
    overline: "[ CLIENT AREA ]",

    logout: "Log out",

    requests: "Your requests",
    noRequests: "No requests yet.",
    newRequest: "New request",

    support: "Support",
    supportEmpty:
      "Have a question? Contact our team and we'll get back to you as soon as possible.",

    placeholder: "Your message…",

    request: "Request",
    supportLabel: "Support",

    status: {
      received: "Received",
      inProgress: "In progress",
      delivered: "Delivered",
    },
  },

  /* =========================================================
     HOME
  ========================================================= */

  home: {
    overline: "[ WEB DESIGN & DIGITAL SOLUTIONS ]",

    title: [
      "Your",
      "digital",
      "presence.",
    ],

    description:
      "HollowCorp is an independent web studio creating modern, fast and custom websites for businesses across France and Europe.",

    cta: "Let's talk about your project",

    servicesTitle:
      "Web solutions designed around your business.",

    servicesDescription:
      "Two offers: a showcase website (presentation, contact, simple hosting) or a web app (client area, emails, application server). Price follows time and resources, not a one-size plan.",
  },

  /* =========================================================
     ABOUT
  ========================================================= */

  about: {
    overline: "[ ABOUT HOLLOWCORP ]",

    title: [
      "Your",
      "digital",
      "presence.",
    ],

    description:
      "HollowCorp is an independent web studio helping businesses build modern, fast and custom websites with a strong focus on design, performance, user experience and SEO.",

    approachOverline: "[ OUR APPROACH ]",

    approachTitle: [
      "The web,",
      "without the noise.",
    ],

    approachParagraph1:
      "We believe a good website should be useful first. It should clearly communicate what your business does, showcase your services and help visitors quickly find the information they need.",

    approachParagraph2:
      "Every HollowCorp website is designed from scratch with attention to visual identity, performance, mobile experience, accessibility and SEO fundamentals. The result is a professional website built to perform across devices.",

    approachParagraph3:
      "From the first idea to launch and beyond, we provide ongoing support including website design, web development, hosting, maintenance, security and continuous improvements.",

    manifestoOverline: "[ OUR MANIFESTO ]",

    manifestoTitle: [
      "Our way of",
      "designing the web.",
    ],

    manifesto: [
      {
        num: "01",

        title: "The web without compromise",

        body:
          "We do not rely on generic templates. Every project is designed around your brand, your goals, your audience and your business.", 
      },

      {
        num: "02",

        title: "All-inclusive, no surprises",

        body:
          "Hosting, domain, security, SEO and Google Business Profile support — one clear plan with ongoing assistance.",
      },

      {
        num: "03",

        title: "Close and responsive",

        body:
          "You have a direct point of contact who understands your project and helps you move forward without unnecessary complexity or jargon.",
      },

      {
        num: "04",

        title: "Built to last",

        body:
          "Fast, secure and scalable websites. Performance, technical foundations, SEO and maintenance are considered from the beginning.",
      },
    ],

    franceOverline: "[ WEB DESIGN ACROSS FRANCE ]",

    franceTitle: [
      "A web studio",
      "with a national reach.",
    ],

    franceParagraph1:
      "HollowCorp works with businesses and professionals across France on website design, development and digital projects, primarily remotely.",

    franceParagraph2:
      "We work with businesses across a wide range of industries, including craftspeople, retailers, restaurants, freelancers, small businesses and professional services.",

    franceParagraph3:
      "Whether you are launching a new business, building your first website or redesigning an existing one, we adapt the project to your goals, your audience and your business.",

    expertiseOverline: "[ OUR EXPERTISE ]",

    expertiseTitle: [
      "Web solutions",
      "for your business.",
    ],

    expertiseDescription:
      "Explore HollowCorp's web design and development services, from custom business websites and e-commerce to web applications, SEO, hosting and maintenance.",

    expertiseButton:
      "Web development",

    expertiseCta:
      "Request a quote",

    projectOverline: "[ YOUR PROJECT ]",

    projectTitle: [
      "Your project deserves",
      "a real website.",
    ],

    projectDescription:
      "Tell us about your business, your goals and your project. We'll help you find the right way to build or improve your online presence.",

    projectButton:
      "Start my project",
  },

  /* =========================================================
     SERVICES
  ========================================================= */

  services: {
    overline: "[ HOLLOWCORP — WEB SERVICES ]",

    heroLines: [
      "Our",
      "web services",
    ],

    heroDescription:
      "HollowCorp designs and develops modern, fast and custom websites for businesses across France and Europe. From strategy and design to development, SEO and ongoing maintenance, we provide the essential services needed for a strong online presence.",

    /* -------------------------------------------------------
       SERVICES LIST
    ------------------------------------------------------- */

    list: [
      {
        id: "01",

        title: "Website design & development",

        description:
          "Custom business websites and e-commerce websites designed around your brand, your goals and your customers. Distinctive design, strong performance and responsive experiences across all devices.",

        tags: [
          "Business websites",
          "E-commerce",
          "Custom design",
        ],
      },

      {
        id: "02",

        title: "Website redesign & modernization",

        description:
          "Is your current website outdated? We redesign its structure, visual identity and user experience while maintaining strong technical foundations and protecting your SEO.",

        tags: [
          "Redesign",
          "Migration",
          "UX",
        ],
      },

      {
        id: "03",

        title: "Web applications",

        description:
          "Custom and scalable web applications including client portals, dashboards, booking systems, internal tools and back-office platforms — from concept to deployment.",

        tags: [
          "Full-stack",
          "Dashboard",
          "Web apps",
        ],
      },

      {
        id: "04",

        title: "SEO, consulting & maintenance",

        description:
          "Ongoing technical support, SEO fundamentals, security updates and website maintenance to keep your online presence fast, secure, reliable and up to date.",

        tags: [
          "SEO",
          "Security",
          "Maintenance",
        ],
      },
    ],

    /* -------------------------------------------------------
       BUSINESS TYPES
    ------------------------------------------------------- */

    businessOverline: "[ FOR YOUR BUSINESS ]",

    businessTitle: "A website tailored to",

    businessTitleAccent: "your business",

    businessDescription:
      "Whether you are a craftsperson, retailer, restaurant, freelancer, professional service, small business or growing company, HollowCorp adapts each website project to your goals and audience.",

    businessTypes: [
      "Craft businesses",
      "Retail businesses",
      "Restaurants",
      "Freelancers",
      "Small & medium businesses",
      "Associations",
      "Professional services",
      "Companies",
    ],

    /* -------------------------------------------------------
       PROCESS
    ------------------------------------------------------- */

    processOverline: "[ THE HOLLOWCORP METHOD ]",

    processTitle: "How it",

    processAccent: "works",

    processDescription:
      "A clear and transparent process: understand your business, define the right strategy, design the experience, develop the website and provide ongoing support after launch.",

    process: [
      {
        num: "01",

        title: "Discovery",

        body:
          "We discuss your business, goals, audience and requirements. You receive a clear proposal tailored to your project.",
      },

      {
        num: "02",

        title: "Design",

        body:
          "We define the visual identity, page structure, content hierarchy and user experience of your website.",
      },

      {
        num: "03",

        title: "Development",

        body:
          "Your website is developed from scratch with responsive design, fast performance and strong technical and SEO foundations.",
      },

      {
        num: "04",

        title: "Launch & support",

        body:
          "After launch, HollowCorp provides hosting, maintenance, security updates and ongoing improvements for your website.",
      },
    ],

    /* -------------------------------------------------------
       MODIFICATIONS
    ------------------------------------------------------- */

    modificationsOverline: "[ WEBSITE UPDATES ]",

    modificationsTitle:
      "Your website evolves.",

    modificationsAccent:
      "Without surprises.",

    modificationsDescription:
      "Your subscription includes website updates so your project can evolve over time. Update your content and features without having to rebuild your website from scratch.",

    modifications: [
      {
        title: "Major changes",

        badge: "2 / semester",

        body:
          "Two major changes are included every semester. One additional major change can be requested for €25.",
      },

      {
        title: "Small changes",

        badge: "5 / 2 months",

        body:
          "Up to 5 small changes are included every 2 months, allowing your website to evolve regularly.",
      },

      {
        title: "Issues caused by HollowCorp",

        badge: "100% included",

        body:
          "Corrections required because of bugs, errors or technical issues caused by HollowCorp are fully covered.",
      },
    ],

    modificationsNote:
      "These limits only apply to content or feature changes requested by the client. Bug fixes, errors and technical issues directly caused by HollowCorp are fully covered and do not count towards these limits.",

    /* -------------------------------------------------------
       ONLINE PRESENCE
    ------------------------------------------------------- */

    onlineOverline:
      "[ ONLINE PRESENCE ]",

    onlineTitle:
      "Build an online presence",

    onlineAccent:
      "that represents you",

    onlineParagraph1:
      "A professional website should be useful to both your business and your visitors. HollowCorp focuses on structure, user experience, accessibility and performance to create a consistent experience across desktop, tablet and mobile.",

    onlineParagraph2:
      "SEO fundamentals are integrated from the beginning: clear page structure, relevant content, essential metadata, technical performance and a logical information architecture.",

    onlineParagraph3:
      "The goal is simple: help visitors understand your business quickly, discover your services and contact you with ease.",

    /* -------------------------------------------------------
       PRICING
    ------------------------------------------------------- */

    pricingOverline: "[ PRICING — TWO OFFERS ]",

    pricingTitle: [
      "Two offers,",
    ],

    pricingAccent:
      "no surprises",

    firstMonth:
      "first month",

    perMonth:
      "/ month afterwards",

    pricingButton:
      "Request a quote",

    offers: {
      overline: "[ PRICING — TWO OFFERS ]",
      title: "Two offers,",
      accent: "no surprises",
      intro:
        "A showcase site presents your business. A web app goes further (client area, emails, back office): more time, more servers, so it costs more — especially the first month.",
      cta: "Request this offer",
      note:
        "The first month covers design and launch. The monthly fee then covers hosting, domain and small updates. A web app stays more expensive each month because it runs on an application server, not just simple website hosting.",
      vitrine: {
        name: "Showcase website",
        badge: "The simpler one",
        desc: "Like L'Atelier du Quai: home, menu or services, contact. Ideal for restaurants, trades and local businesses.",
        included: [
          "Hosting and domain included",
          "Responsive site (phone and desktop)",
          "Basic SEO (Google)",
          "Up to 3 small updates every 2 months",
          "No member area or backend",
        ],
      },
      webapp: {
        name: "Web app",
        badge: "More time, more power",
        desc: "Like hollowcorp.fr: forms, emails, client area, admin. Higher first month: build time and servers.",
        included: [
          "Website hosting + application server",
          "Transactional emails included",
          "Client area / custom features",
          "SEO, domain and security",
          "2 major updates / semester + 5 small ones / 2 months",
        ],
      },
    },

    included: [
      "Web hosting included",
      "Free domain name",
      "SEO improvements",
      "Website updates",
      "Regular security updates",
      "Google Business Profile management",
    ],

    includedChanges:
      "Included website updates",

    includedChangesDescription:
      "2 major changes per semester and up to 5 small changes every 2 months. One additional major change is available for €25.",

    /* -------------------------------------------------------
       MARQUEE
    ------------------------------------------------------- */

    marquee: [
      "HollowCorp",
      "Web design",
      "Web development",
      "Business websites",
      "Custom websites",
      "E-commerce",
      "SEO",
      "Web hosting",
      "Website maintenance",
    ],
  },

  /* =========================================================
     TESTIMONIALS
  ========================================================= */

  testimonials: {
    overline: "[ EXAMPLES ]",

    title: "Two ways",

    titleAccent: "to exist online.",

    reviews: [
      {
        quote:
          "L'Atelier du Quai is a fictional restaurant: home, menu, hours, contact. That is exactly what a showcase website delivers — with no app behind it.",

        author: "L'Atelier du Quai",

        role: "Showcase website — HollowCorp example",
      },

      {
        quote:
          "hollowcorp.fr is a web app: forms, emails, client area, admin. Longer to build, more expensive to host, so a higher first month and monthly fee.",

        author: "hollowcorp.fr",

        role: "Web app — HollowCorp’s own site",
      },
    ],
  },

  /* =========================================================
     FOOTER
  ========================================================= */

  footer: {
    ctaLine1: "Let's talk about",

    ctaLine2: "your project",

    description:
      "A professional website with hosting, maintenance and ongoing support. One clear plan and a dedicated point of contact.",

    contact: "Contact",

    socials: "Socials",

    instagram: "Instagram",

    tiktok: "TikTok",

    legal: "Legal notice & GDPR",

    personalArea: "Personal area",
  },

  /* =========================================================
     ADMIN
  ========================================================= */

  admin: {
    roles: {
      ceo: "CEO",
      manager: "Manager",
      employee: "Employee",
      client: "Client",
      staff: "Staff",
    },

    login: {
      overline: "[ STAFF AREA ]",
      title: "Admin.",

      ceoTab: "CEO",
      employeeTab: "Manager / Employee",

      adminDescription:
        "Admin access. Click to receive a one-time login code at your HollowCorp email address.",

      receiveCode: "Receive my code",

      codeSent: "Code sent to",
      codeValidity: "valid for 10 min",
      codeError: "Unable to send the code",

      codeLabel: "Email verification code",
      codePlaceholder: "123456",

      invalidCode: "Invalid code",
      ceoLogin: "CEO Login",

      emailLabel: "Email",
      emailPlaceholder: "you@hollowcorp.fr",

      passwordLabel: "Password",
      passwordPlaceholder: "••••••••",

      invalidCredentials: "Invalid credentials",
      employeeLogin: "Login",
    },

    dashboard: {
      title: "Dashboard",
      logout: "Log out",

      requests: "Requests",
      support: "Support",
      team: "Team",

      permissions: {
        ceo: "Full access",
        manager: "Operational management",
        employee: "Employee access",
      },
    },

    requests: {
      title: "Requests",

      empty: "No requests received.",
      error: "Unable to load requests.",

      defaultProject: "Request",

      received: "Received",
      inProgress: "In progress",
      delivered: "Delivered",

      statusChanged: "Status →",
      deleted: "Request deleted",

      readOnly: "View only",
      updateStatus: "Update status",
      deleteRequest: "Delete request",
    },

    support: {
      title: "Support",

      empty: "No conversations.",
      selectConversation: "Select a conversation",

      replyPlaceholder: "Reply…",
      send: "Send",

      resetPassword: "Reset password",

      newPassword: "New:",

      forwardPassword:
        "Forward this temporary password to the person. It is not sent by email.",

      close: "Close",

      passwordReset: "Password reset",

      conversationDeleted:
        "Conversation deleted",

      resetError:
        "An error occurred.",

      deleteConversation:
        "Delete chat",

      clientPasswordReset:
        "Reset client password",

      reply:
        "Reply to client",

      delete:
        "Delete conversation",
    },

    team: {
      title: "Team",

      recruit: "Recruit",
      members: "Team",

      name: "Name",
      email: "Email",
      password: "Password (min. 6)",

      role: "Role",

      employee: "Employee",
      manager: "Manager",

      managerOnlyCeo:
        "(CEO only)",

      createAccount:
        "Create account",

      created: "created",

      deleted:
        "Member deleted",

      empty:
        "No members.",

      error:
        "An error occurred.",

      resetPassword:
        "Reset password",

      delete:
        "Delete",

      permissions: {
        ceo: {
          title:
            "Full team management",

          createEmployee:
            "Create employee",

          createManager:
            "Create manager",

          deleteEmployee:
            "Delete employee",

          deleteManager:
            "Delete manager",

          resetPassword:
            "Reset passwords",
        },

        manager: {
          title:
            "Employee management",

          createEmployee:
            "Create employee",

          createManager:
            "Create manager",

          deleteEmployee:
            "Delete employee",

          deleteManager:
            "Delete manager",

          resetPassword:
            "Reset passwords",
        },

        employee: {
          title:
            "Employee access",

          createEmployee:
            "Create employee",

          createManager:
            "Create manager",

          deleteEmployee:
            "Delete employee",

          deleteManager:
            "Delete manager",

          resetPassword:
            "Reset passwords",
        },
      },
    },

    access: {
      ceo: {
        label: "CEO",

        description:
          "Full access to HollowCorp administration.",

        requests: {
          view: true,
          update: true,
          delete: true,
        },

        support: {
          view: true,
          reply: true,
          delete: true,
          resetPassword: true,
        },

        team: {
          view: true,
          createEmployee: true,
          createManager: true,
          deleteEmployee: true,
          deleteManager: true,
          resetPassword: true,
        },
      },

      manager: {
        label: "Manager",

        description:
          "Operational management of the team and clients.",

        requests: {
          view: true,
          update: true,
          delete: false,
        },

        support: {
          view: true,
          reply: true,
          delete: false,
          resetPassword: true,
        },

        team: {
          view: true,
          createEmployee: true,
          createManager: false,
          deleteEmployee: true,
          deleteManager: false,
          resetPassword: true,
        },
      },

      employee: {
        label: "Employee",

        description:
          "Access to tasks and client support.",

        requests: {
          view: true,
          update: true,
          delete: false,
        },

        support: {
          view: true,
          reply: true,
          delete: false,
          resetPassword: false,
        },

        team: {
          view: false,
          createEmployee: false,
          createManager: false,
          deleteEmployee: false,
          deleteManager: false,
          resetPassword: false,
        },
      },

      client: {
        label: "Client",

        description:
          "Client-only access.",

        requests: {
          view: false,
          update: false,
          delete: false,
        },

        support: {
          view: false,
          reply: false,
          delete: false,
          resetPassword: false,
        },

        team: {
          view: false,
          createEmployee: false,
          createManager: false,
          deleteEmployee: false,
          deleteManager: false,
          resetPassword: false,
        },
      },
    },

    permissions: {
      denied:
        "You do not have the required permissions.",

      ceoOnly:
        "This action is restricted to the CEO.",

      managerOnly:
        "This action is restricted to the CEO or Manager.",

      employeeOnly:
        "This action is restricted to employees.",
    },

    errors: {
      unauthorized:
        "Unauthorized access.",

      forbidden:
        "You do not have access to this feature.",

      server:
        "Server error.",

      network:
        "Unable to contact the server.",

      unknown:
        "An error occurred.",
    },

    success: {
      saved:
        "Changes saved.",

      updated:
        "Update completed.",

      deleted:
        "Deletion completed.",

      created:
        "Account created.",
    },
  },
};

export default en;